/**
 * ToolCallBubble — one tool call in a transcript, as a chip that expands.
 *
 * Collapsed: the tool's icon, its short name, a one-line preview of the
 * arguments, how long it took and its status. Expanded: the full arguments
 * and the result (or error), each copyable.
 *
 * States:
 *   pending  → icon pulses, spinner
 *   ok       → tick
 *   error    → danger border, expanded by default with the error text
 *
 * The chat, a task's transcript and the landing page's demo all render
 * this one component; ``ToolCallGroup`` stacks several.
 */
import * as React from "react";
import { AlertCircle, Check, ChevronRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { CopyAction } from "./CopyAction";
import { argsPreview, formatDuration, toolIcon, toolShortName } from "./toolMeta";

export interface ToolCallView {
  tool_use_id: string;
  name: string;
  arguments: Record<string, unknown>;
  status: "pending" | "ok" | "error";
  result?: unknown;
  /** Wall time of the call, when the source reports it. */
  durationMs?: number;
  /** The tool's declared icon word (backend ``Tool.icon``). */
  icon?: string | null;
  /** Local id of the user message that triggered this call. Tool calls
   *  render directly after the user bubble they belong to. Null when
   *  the association can't be established (reloaded sessions, stale
   *  events, etc.) — those orphan calls render at the end of the
   *  thread instead. */
  messageId?: string | null;
}

const MAX_SHOWN = 4000;
const ROW = "flex w-full items-center gap-2.5 px-2.5 py-2 text-left";

function asText(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function CodeBlock({ label, value, tone }: { label: string; value: unknown; tone?: "danger" }) {
  const text = asText(value);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className={cn("font-mono text-[10px] uppercase tracking-wider", tone === "danger" ? "text-danger" : "text-fg-faint")}>
          {label}
        </span>
        <CopyAction text={text} ariaLabel={`Copy ${label.toLowerCase()}`} />
      </div>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words bg-bg-sunken p-2 font-mono text-[11px] text-fg">
        {text.length > MAX_SHOWN ? `${text.slice(0, MAX_SHOWN)}…` : text}
      </pre>
    </div>
  );
}

export function ToolCallBubble({ call }: { call: ToolCallView }) {
  const [open, setOpen] = React.useState(call.status === "error");
  const detailsId = React.useId();
  // A call that fails after it was shown opens to show why.
  React.useEffect(() => {
    if (call.status === "error") setOpen(true);
  }, [call.status]);

  const Icon = toolIcon(call.name, call.icon);
  const preview = argsPreview(call.arguments);
  const hasArgs = Object.keys(call.arguments).length > 0;
  const hasResult = call.status !== "pending" && call.result !== undefined && call.result !== null;
  const expandable = hasArgs || hasResult;
  const statusLabel = call.status === "pending" ? "Running" : call.status === "ok" ? "Done" : "Failed";

  const row = (
    <>
      <span
        className={cn(
          "flex h-6 w-6 flex-shrink-0 items-center justify-center border",
          call.status === "pending"
            ? "animate-pulse border-accent/50 bg-accent-bg text-accent"
            : call.status === "error"
              ? "border-danger/40 text-danger"
              : "border-border bg-bg-sunken text-fg-muted",
        )}
        aria-hidden
      >
        <Icon size={13} />
      </span>
      <span className="flex min-w-0 flex-1 items-baseline gap-2 font-mono">
        <span className="flex-shrink-0 text-fg">{toolShortName(call.name)}</span>
        {preview && <span className="truncate text-fg-faint">› {preview}</span>}
      </span>
      {call.durationMs !== undefined && call.status !== "pending" && (
        <span className="flex-shrink-0 font-mono text-[10px] text-fg-faint">{formatDuration(call.durationMs)}</span>
      )}
      <span className="flex-shrink-0" title={statusLabel}>
        {call.status === "pending" ? (
          <Loader2 size={14} className="animate-spin text-accent" aria-hidden />
        ) : call.status === "ok" ? (
          <Check size={14} className="text-success" aria-hidden />
        ) : (
          <AlertCircle size={14} className="text-danger" aria-hidden />
        )}
        <span className="sr-only">{statusLabel}</span>
      </span>
      {expandable && (
        <ChevronRight
          size={12}
          aria-hidden
          className={cn("flex-shrink-0 text-fg-faint transition-transform", open && "rotate-90")}
        />
      )}
    </>
  );

  return (
    <div
      className={cn(
        "border bg-bg-elevated text-xs transition-colors",
        call.status === "error" ? "border-danger/50" : "border-border",
      )}
      data-testid={`tool-call-${call.tool_use_id}`}
      data-tool-status={call.status}
    >
      {expandable ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={detailsId}
          title={call.name}
          className={cn(ROW, "hover:bg-bg-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus")}
        >
          {row}
        </button>
      ) : (
        // Nothing to reveal yet (a call with no arguments, still running):
        // a status row, not a button that does nothing when pressed.
        <div title={call.name} className={ROW}>
          {row}
        </div>
      )}
      {expandable && open && (
        <div id={detailsId} className="border-t border-border px-3 py-2">
          <div className="space-y-2">
            {hasArgs && <CodeBlock label="Arguments" value={call.arguments} />}
            {hasResult && (
              <CodeBlock
                label={call.status === "ok" ? "Result" : "Error"}
                value={call.result}
                tone={call.status === "error" ? "danger" : undefined}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Calls that collapse into one summary row once there are more than this. */
const GROUP_AFTER = 3;

/**
 * A run of tool calls. A few render as a stack; a long run (an agent that
 * searched ten times) collapses to "Used N tools" with their icons, and
 * stays open while any of them is still running.
 */
export function ToolCallGroup({
  calls,
  className,
  ...rest
}: { calls: ToolCallView[]; className?: string } & React.HTMLAttributes<HTMLDivElement>) {
  const running = calls.some((c) => c.status === "pending");
  const failed = calls.filter((c) => c.status === "error").length;
  const [expanded, setExpanded] = React.useState(false);
  const open = calls.length <= GROUP_AFTER || expanded || running;

  return (
    <div className={cn("space-y-1.5", className)} {...rest}>
      {calls.length > GROUP_AFTER && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center gap-2 border border-dashed border-border px-2.5 py-1.5 text-left text-xs text-fg-muted hover:bg-bg-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus"
        >
          <span className="flex -space-x-1" aria-hidden>
            {calls.slice(0, 5).map((c) => {
              const Icon = toolIcon(c.name, c.icon);
              return (
                <span key={c.tool_use_id} className="flex h-5 w-5 items-center justify-center border border-border bg-bg-elevated">
                  <Icon size={11} />
                </span>
              );
            })}
          </span>
          <span className="flex-1">
            {running ? "Using" : "Used"} {calls.length} tools
            {failed > 0 && <span className="text-danger"> · {failed} failed</span>}
          </span>
          <ChevronRight size={12} aria-hidden className={cn("text-fg-faint transition-transform", open && "rotate-90")} />
        </button>
      )}
      {open && calls.map((call) => <ToolCallBubble key={call.tool_use_id} call={call} />)}
    </div>
  );
}
